import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Tabs,
  Tab,
  Link,
  Divider
} from '@mui/material';
import { Add, Edit, Delete, Search, Visibility, Upload, Link as LinkIcon, OpenInNew } from '@mui/icons-material';
import { usersService } from '../services/users';
import { companiesApi } from '../services/companies';
import { documentsService } from '../services/documents';
import { UserList, UserForm, User, CompanyList, Document, UserDocument } from '../types';
import { format } from 'date-fns';
import { useAuth } from '../hooks/useAuth';

const UsersPage: React.FC = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [users, setUsers] = useState<UserList[]>([]);
  const [allUsers, setAllUsers] = useState<UserList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false); // Режим просмотра (только чтение)
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [companies, setCompanies] = useState<CompanyList[]>([]);
  const [formLoading, setFormLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [userDocuments, setUserDocuments] = useState<UserDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [newDocumentUrl, setNewDocumentUrl] = useState('');
  const [newDocumentId, setNewDocumentId] = useState<number | ''>('');
  const [newDocumentNotes, setNewDocumentNotes] = useState('');
  const [newDocumentExpiresAt, setNewDocumentExpiresAt] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [editingExpiresDoc, setEditingExpiresDoc] = useState<UserDocument | null>(null);
  const [editExpiresAt, setEditExpiresAt] = useState('');
  const [expiresSaving, setExpiresSaving] = useState(false);

  const roleColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    admin: 'error',
    ae: 'warning',
    employee: 'info',
    client: 'primary',
    partner: 'secondary',
    csm: 'info'
  };

  const roleLabels: Record<string, string> = {
    admin: 'Administrator',
    ae: 'AE (Account Executive)',
    employee: 'Employee',
    client: 'Client',
    partner: 'Partner',
    csm: 'CSM (Customer Success Manager)'
  };

  // Form state
  const [formData, setFormData] = useState<UserForm>({
    first_name: '',
    last_name: '',
    middle_name: '',
    email: '',
    phone: '',
    is_active: true,
    role: 'client',
    address_1: '',
    address_2: '',
    company_id: undefined
  });

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await usersService.getUsers();
      // Нормализуем роли для обратной совместимости
      const normalizedData = data.map(user => ({
        ...user,
        role: normalizeRole(user.role)
      }));
      setAllUsers(normalizedData);
    } catch (err: any) {
      console.error('Error loading users:', err);
      const errorDetail = err.response?.data?.detail;
      setError(formatError(errorDetail) || 'Error loading users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = (data: UserList[], search: string, onlyActive: boolean) => {
    let filtered = [...data];
    
    // Фильтр по поиску (по имени, фамилии или email)
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(user => 
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
      );
    }
    
    // Фильтр по активности
    if (onlyActive) {
      filtered = filtered.filter(user => user.is_active);
    }
    
    setUsers(filtered);
  };

  const handleSearch = () => {
    filterUsers(allUsers, searchTerm, showOnlyActive);
  };

  const loadCompanies = async () => {
    try {
      const data = await companiesApi.getCompanies();
      setCompanies(data);
    } catch (err: any) {
      console.error('Error loading companies:', err);
    }
  };

  const loadUserDetails = async (id: number) => {
    try {
      setFormLoading(true);
      const user = await usersService.getUser(id);
      // Нормализуем роль для обратной совместимости
      const normalizedUser = {
        ...user,
        role: normalizeRole(user.role) as any
      };
      setSelectedUser(normalizedUser);
      return normalizedUser;
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      setError(formatError(errorDetail) || 'Error loading user data');
      return null;
    } finally {
      setFormLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const data = await documentsService.getDocuments();
      setDocuments(data);
    } catch (err: any) {
      console.error('Error loading document types:', err);
    }
  };

  const loadUserDocuments = async (userId: number) => {
    try {
      setDocumentsLoading(true);
      const data = await documentsService.getUserDocuments(userId);
      setUserDocuments(data);
    } catch (err: any) {
      console.error('Error loading user documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleAddDocumentByUrl = async () => {
    if (!selectedUser || !newDocumentId || !newDocumentUrl) {
      setError('Please fill in all fields');
      return;
    }
    try {
      await documentsService.createUserDocument(selectedUser.id, {
        document_id: newDocumentId as number,
        url: newDocumentUrl,
        notes: newDocumentNotes || undefined,
        expires_at: newDocumentExpiresAt || null,
        is_verified: false
      });
      setNewDocumentUrl('');
      setNewDocumentId('');
      setNewDocumentNotes('');
      setNewDocumentExpiresAt('');
      await loadUserDocuments(selectedUser.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error adding document');
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedUser || !newDocumentId || !uploadFile) {
      setError('Please select document type and file');
      return;
    }
    try {
      await documentsService.uploadUserDocument(
        selectedUser.id,
        newDocumentId as number,
        uploadFile,
        newDocumentNotes || undefined,
        newDocumentExpiresAt || null
      );
      setUploadFile(null);
      setNewDocumentId('');
      setNewDocumentNotes('');
      setNewDocumentExpiresAt('');
      await loadUserDocuments(selectedUser.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error uploading file');
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!selectedUser) return;
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentsService.deleteUserDocument(documentId);
        await loadUserDocuments(selectedUser.id);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Error deleting document');
      }
    }
  };

  const openEditExpires = (doc: UserDocument) => {
    setEditingExpiresDoc(doc);
    setEditExpiresAt(doc.expires_at ? doc.expires_at.slice(0, 10) : '');
  };

  const handleSaveExpires = async () => {
    if (!selectedUser || !editingExpiresDoc) return;
    try {
      setExpiresSaving(true);
      await documentsService.updateUserDocument(editingExpiresDoc.id, {
        expires_at: editExpiresAt || null
      });
      setEditingExpiresDoc(null);
      setEditExpiresAt('');
      await loadUserDocuments(selectedUser.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error updating expiration date');
    } finally {
      setExpiresSaving(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadCompanies();
    loadDocuments();
  }, []);

  useEffect(() => {
    filterUsers(allUsers, searchTerm, showOnlyActive);
  }, [allUsers, searchTerm, showOnlyActive]);

  useEffect(() => {
    if (selectedUser) {
      loadUserDocuments(selectedUser.id);
    } else {
      setUserDocuments([]);
    }
  }, [selectedUser]);

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleOpenUser = async (userId: number) => {
    const user = await loadUserDetails(userId);
    if (user) {
      // Для UsersPage все могут редактировать (можно добавить проверку прав позже)
      setIsViewMode(false);

      setActiveTab(0);
      setFormData({
        first_name: user.first_name,
        last_name: user.last_name,
        middle_name: user.middle_name || '',
        email: user.email,
        phone: user.phone || '',
        is_active: user.is_active,
        role: normalizeRole(user.role) as any,
        address_1: user.address_1 || '',
        address_2: user.address_2 || '',
        company_id: user.company_id
      });
      setOpenEditDialog(true);
    }
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsViewMode(false);
    setActiveTab(0);
    setNewPassword('');
    setFormData({
      first_name: '',
      last_name: '',
      middle_name: '',
      email: '',
      phone: '',
      is_active: true,
      role: 'client',
      address_1: '',
      address_2: '',
      company_id: undefined
    });
    setOpenEditDialog(true);
  };

  // Маппинг ролей из строковых значений в числовые для бэкенда
  const roleToBackend = (role: string): string => {
    const roleMapping: Record<string, string> = {
      'admin': '1',
      'employee': '2',
      'ae': '3',
      'manager': '3', // Старое значение для обратной совместимости
      'partner': '4',
      'client': '5',
      'csm': '7'
    };
    return roleMapping[role] || '5'; // Default to client
  };

  // Нормализация роли (для обратной совместимости со старыми данными)
  const normalizeRole = (role: string): string => {
    if (role === 'manager') {
      return 'ae';
    }
    return role;
  };

  const handleSaveUser = async () => {
    try {
      setFormLoading(true);
      setError('');
      
      // Преобразуем данные формы в формат, который ожидает бэкенд
      const backendData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        middle_name: formData.middle_name || null,
        phone: formData.phone || null,
        mail: formData.email, // Бэкенд ожидает mail, а не email
        active: formData.is_active, // Бэкенд ожидает active, а не is_active
        address_1: formData.address_1 || null,
        address_2: formData.address_2 || null,
        role: roleToBackend(formData.role), // Преобразуем роль в числовое значение
        company_id: formData.company_id || null
      };
      
      if (selectedUser) {
        // Update existing user
        await usersService.updateUser(selectedUser.id, backendData as any);
      } else {
        // Create new user - password will be generated on backend
        await usersService.createUser({
          ...backendData,
          password: 'temp_password_will_be_generated'
        } as any);
      }
      
      setOpenEditDialog(false);
      await loadUsers();
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      setError(formatError(errorDetail));
    } finally {
      setFormLoading(false);
    }
  };


  const handleChangePassword = async () => {
    if (!selectedUser) return;
    
    if (!newPassword || newPassword.length < 8) {
      setError('Password must contain at least 8 characters');
      return;
    }

    if (!/[a-zA-Z]/.test(newPassword)) {
      setError('Password must contain at least one letter');
      return;
    }

    if (!/\d/.test(newPassword)) {
      setError('Password must contain at least one digit');
      return;
    }

    try {
      setPasswordChanging(true);
      setError('');
      await usersService.setUserPassword(selectedUser.id, newPassword);
      setNewPassword('');
      alert('Password changed successfully');
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      setError(formatError(errorDetail) || 'Error changing password');
    } finally {
      setPasswordChanging(false);
    }
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const formatError = (error: any): string => {
    if (!error) return 'Unknown error';
    
    // If it's an array of validation errors
    if (Array.isArray(error)) {
      return error.map((err: any) => {
        const field = err.loc ? err.loc.join('.') : 'field';
        const message = err.msg || 'Validation error';
        return `${field}: ${message}`;
      }).join('; ');
    }
    
    // If it's a string
    if (typeof error === 'string') {
      return error;
    }
    
    // If it's an object with a message
    if (error.message) {
      return error.message;
    }
    
    return 'Error saving user';
  };

  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Users
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddUser}
        >
          Add User
        </Button>
      </Box>

      <Box display="flex" gap={2} mb={3} alignItems="center">
        <TextField
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            filterUsers(allUsers, e.target.value, showOnlyActive);
          }}
          onKeyPress={handleSearchKeyPress}
          size="small"
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyActive}
              onChange={(e) => {
                setShowOnlyActive(e.target.checked);
                filterUsers(allUsers, searchTerm, e.target.checked);
              }}
            />
          }
          label="Show only active"
        />
        {searchTerm && (
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setSearchTerm('');
              filterUsers(allUsers, '', showOnlyActive);
            }}
          >
            Reset
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>{user.first_name}</TableCell>
                  <TableCell>{user.last_name}</TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => handleOpenUser(user.id)}
                    >
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.phone || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={roleLabels[normalizeRole(user.role)] || normalizeRole(user.role)}
                      color={roleColors[normalizeRole(user.role)] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? 'Active' : 'Inactive'}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Create/View Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setIsViewMode(false);
          setActiveTab(0);
          setNewPassword('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedUser ? (isViewMode ? 'View User' : 'Edit User') : 'Add User'}
        </DialogTitle>
        <DialogContent>
          {formLoading && !selectedUser ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              {selectedUser && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label="Main Information" />
                    <Tab label="Documents" />
                  </Tabs>
                </Box>
              )}
              
              {activeTab === 0 && (
                <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    disabled={isViewMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Middle Name"
                    value={formData.middle_name}
                    onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={isViewMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    disabled={isViewMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                      label="Role"
                      disabled={isViewMode}
                    >
                      <MenuItem value="admin">Administrator</MenuItem>
                      <MenuItem value="ae">AE (Account Executive)</MenuItem>
                      <MenuItem value="employee">Employee</MenuItem>
                      <MenuItem value="client">Client</MenuItem>
                      <MenuItem value="partner">Partner</MenuItem>
                      <MenuItem value="csm">CSM (Customer Success Manager)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        disabled={isViewMode}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address 1"
                    value={formData.address_1}
                    onChange={(e) => setFormData({ ...formData, address_1: e.target.value })}
                    multiline
                    rows={2}
                    disabled={isViewMode}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address 2"
                    value={formData.address_2}
                    onChange={(e) => setFormData({ ...formData, address_2: e.target.value })}
                    multiline
                    rows={2}
                    disabled={isViewMode}
                  />
                </Grid>
                {selectedUser && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <TextField
                        fullWidth
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        helperText="Minimum 6 characters"
                        disabled={isViewMode}
                      />
                      <Button
                        variant="contained"
                        onClick={handleChangePassword}
                        disabled={passwordChanging || !newPassword || newPassword.length < 6 || isViewMode}
                        sx={{ mt: 3.5, height: 56, minWidth: 150, flexShrink: 0 }}
                      >
                        {passwordChanging ? <CircularProgress size={20} /> : 'Change Password'}
                      </Button>
                    </Box>
                  </Grid>
                )}
                {!selectedUser && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      A temporary password will be automatically generated when creating a user
                    </Alert>
                  </Grid>
                )}
              </Grid>
              )}
              
              {activeTab === 1 && selectedUser && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    User Documents
                  </Typography>
                  
                  {/* Add document by URL */}
                  {!isClient && !isViewMode && (
                  <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Add Document by URL
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>Тип документа</InputLabel>
                          <Select
                            value={newDocumentId}
                            onChange={(e) => setNewDocumentId(e.target.value as number | '')}
                            label="Тип документа"
                          >
                            <MenuItem value="">Выберите тип</MenuItem>
                            {documents.map((doc) => (
                              <MenuItem key={doc.id} value={doc.id}>
                                {doc.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Document URL"
                          value={newDocumentUrl}
                          onChange={(e) => setNewDocumentUrl(e.target.value)}
                          placeholder="https://..."
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleAddDocumentByUrl}
                          disabled={!newDocumentId || !newDocumentUrl}
                          startIcon={<LinkIcon />}
                          sx={{ height: '56px' }}
                        >
                          Add
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Expiration Date (optional)"
                          type="date"
                          value={newDocumentExpiresAt}
                          onChange={(e) => setNewDocumentExpiresAt(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Notes (optional)"
                          value={newDocumentNotes}
                          onChange={(e) => setNewDocumentNotes(e.target.value)}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  )}
                  
                  {/* Upload file */}
                  {!isClient && !isViewMode && (
                  <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Upload File
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>Тип документа</InputLabel>
                          <Select
                            value={newDocumentId}
                            onChange={(e) => setNewDocumentId(e.target.value as number | '')}
                            label="Тип документа"
                          >
                            <MenuItem value="">Выберите тип</MenuItem>
                            {documents.map((doc) => (
                              <MenuItem key={doc.id} value={doc.id}>
                                {doc.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          startIcon={<Upload />}
                          sx={{ height: '56px' }}
                        >
                          {uploadFile ? uploadFile.name : 'Select File'}
                          <input
                            type="file"
                            hidden
                            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                          />
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleUploadDocument}
                          disabled={!newDocumentId || !uploadFile}
                          startIcon={<Upload />}
                          sx={{ height: '56px' }}
                        >
                          Upload
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Expiration Date (optional)"
                          type="date"
                          value={newDocumentExpiresAt}
                          onChange={(e) => setNewDocumentExpiresAt(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Notes (optional)"
                          value={newDocumentNotes}
                          onChange={(e) => setNewDocumentNotes(e.target.value)}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  )}
                  
                  {/* Documents list */}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Documents List
                  </Typography>
                  {documentsLoading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : userDocuments.length === 0 ? (
                    <Alert severity="info">No documents added</Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Document Type</TableCell>
                            <TableCell>File/URL</TableCell>
                            <TableCell>Size</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Expires</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userDocuments.map((doc) => (
                            <TableRow key={doc.id}>
                              <TableCell>{doc.document?.name || `ID: ${doc.document_id}`}</TableCell>
                              <TableCell>
                                {doc.url.startsWith('uploads/') || doc.url.startsWith('/uploads/') || doc.url.startsWith('http') ? (
                                  <Link href={doc.url.startsWith('http') ? doc.url : `http://localhost:8000/${doc.url.startsWith('/') ? doc.url.slice(1) : doc.url}`} target="_blank" rel="noopener noreferrer">
                                    {doc.file_name || 'Open'}
                                    <OpenInNew fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                                  </Link>
                                ) : (
                                  doc.url
                                )}
                              </TableCell>
                              <TableCell>
                                {doc.file_size ? `${(doc.file_size / 1024).toFixed(2)} KB` : '-'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={doc.is_verified ? 'Verified' : 'Not Verified'}
                                  color={doc.is_verified ? 'success' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{format(new Date(doc.created_at), 'dd.MM.yyyy')}</TableCell>
                              <TableCell>
                                {doc.expires_at ? format(new Date(doc.expires_at), 'dd.MM.yyyy') : '—'}
                              </TableCell>
                              <TableCell>
                                {!isClient && !isViewMode && (
                                <>
                                <IconButton
                                  size="small"
                                  onClick={() => openEditExpires(doc)}
                                  title="Edit expiration date"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  color="error"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                                </>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            // Не сбрасываем isViewMode для клиентов, чтобы избежать кратковременного отображения редактируемых полей и кнопки Save
            if (!isClient) {
              setIsViewMode(false);
            }
            setActiveTab(0);
            setNewPassword('');
          }} disabled={formLoading}>
            {isViewMode || isClient ? 'Close' : 'Cancel'}
          </Button>
          {!isClient && !isViewMode && (
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={formLoading || !formData.first_name || !formData.last_name || !formData.email}
          >
              {formLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editingExpiresDoc}
        onClose={() => {
          setEditingExpiresDoc(null);
          setEditExpiresAt('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Expiration Date</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Expiration Date"
            type="date"
            value={editExpiresAt}
            onChange={(e) => setEditExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty to clear expiration date"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditingExpiresDoc(null);
              setEditExpiresAt('');
            }}
            disabled={expiresSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveExpires}
            variant="contained"
            disabled={expiresSaving}
          >
            {expiresSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsersPage;
